import React, { useContext, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import Typography from '@material-ui/core/Typography';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import { PBXCallState } from 'app/store/contexts';
import useLogout from 'app/auth/useLogout';

const IDLE_TIMEOUT = 15 * 60 * 1000;
const CHECK_INTERVAL = 5 * 1000;
const LAST_ACTIVITY_KEY = 'pbxIdleLastActivityAt';
const DIALER_PERMISSION_KEY = 'FRONT:/apps/callcenter/dialer';

const hasDialerPermission = () => {
	try {
		const acl = JSON.parse(localStorage.getItem('user_acl'));
		return acl?.[DIALER_PERMISSION_KEY] === 1;
	} catch (e) {
		return false;
	}
};

const markActive = () => {
	localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
};

const isPastIdleTimeout = () => {
	const lastActivityAt = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
	if (!lastActivityAt) return false;
	return Date.now() - lastActivityAt >= IDLE_TIMEOUT;
};

// Only reconnects the PBX dialer agent on "Still here" - it must never touch
// anything else (no reload, no full re-login) per the feature request.
const IdleTimeoutDialog = () => {
	const { agentStatus, sipConnect, sipDisconnect } = useContext(PBXCallState) || {};
	const { logout } = useLogout();
	const [open, setOpen] = useState(isPastIdleTimeout);
	// Read inside the onAction closure below, which react-idle-timer only
	// binds once - a plain variable would always see the initial `false`.
	const openRef = useRef(open);
	openRef.current = open;

	// The timeout itself lives in localStorage, not react-idle-timer's
	// in-memory state, so a page reload doesn't silently reset the clock -
	// this only uses the library to detect + throttle activity events.
	useIdleTimer({
		onAction: () => {
			// Once the prompt is up, only its own buttons may clear it - otherwise
			// the keystroke used to trigger a page reload (a real 'keydown' the
			// page sees a moment before it unloads) would mark the user "active"
			// and the reloaded page would never know it had been idle.
			if (openRef.current) return;
			markActive();
		},
		eventsThrottle: CHECK_INTERVAL
	});

	useEffect(() => {
		if (!localStorage.getItem(LAST_ACTIVITY_KEY)) markActive();

		const interval = setInterval(() => {
			if (isPastIdleTimeout()) setOpen(true);
		}, CHECK_INTERVAL);

		return () => clearInterval(interval);
	}, []);

	const handleStillHere = async () => {
		markActive();
		setOpen(false);

		if (agentStatus === 'available' && sipDisconnect && sipConnect) {
			await sipDisconnect();
			sipConnect();
		}
	};

	const handleLogout = async () => {
		// Auto-disconnect the call center instead of popping a second
		// confirmation on top of this dialog - the header's Logout keeps asking.
		const success = await logout({ autoDisconnectPbx: true });
		if (success) {
			setOpen(false);
		}
	};

	return (
		<ModalOmni
			open={open}
			title="Are you still there?"
			disableBackdropClick
			disableEscapeKeyDown
			hideCloseButton
			customWidth={420}
			content={
				<div style={{ padding: '1.5em' }}>
					<Typography>
						You've been inactive for a while. Stay signed in to keep your PBX call agent connected, or log
						out.
					</Typography>
				</div>
			}
			actions={
				<>
					<ButtonOmni
						label="I'm Still Here"
						onClick={handleStillHere}
						variant="contained"
						color="secondary"
					/>
					<ButtonOmni label="Logout" onClick={handleLogout} />
				</>
			}
		/>
	);
};

// Keeps the idle timer from ever being created for users without dialer
// access, instead of creating it and just ignoring onIdle.
const IdleTimeoutGate = () => {
	const [enabled] = useState(hasDialerPermission);

	if (!enabled) return null;

	return <IdleTimeoutDialog />;
};

export default IdleTimeoutGate;
